import React, { useState, useEffect, useMemo } from 'react';
import { Field } from 'redux-form/immutable';
import classNames from 'classnames/dedupe';
import { connect, useDispatch, useSelector } from 'react-redux';
import { injectIntl, intlShape } from 'react-intl';
import Big from 'big.js';
import { Container } from 'cstar-react-primitives/lib/redux-form/layout/Container';
import PropTypes from 'prop-types';
import { getItemCurrencyList, getSubTotal } from '../item/itemSelectors';
import {
    getElectronicDenoms,
    getMaxEgcValue,
    getMaxPlasticValue, getMaxTransactionAmount,
    getMinEgcValue, getMinPlasticValue, getPlasticDenoms,
    isFixedEgcCatalogPresent,
    isFixedPlasticCatalogPresent
} from '../catalogs/catalogSelectors';
import {
    giftCardFieldNames,
    newItemCurrency,
    newItemDenomination,
    newItemSelectedGroupOrdinal
} from '../item/newItemForm';
import CustomAmount, { customAmountNumberFormatter } from '../amount/CustomAmount';
import amountMessages from '../amount/amountMessages';
import { getCurrencySymbol } from '../intl/getCurrencySymbol';
import { getFormattedAmountWithCurrency, fixDecimalFormat, getCurrencyFractionDigits } from '../utils/numberUtils';
import { DenomPickerField } from '../denomination/DenomPickerField';
import CardType from './CardType';
import { getDigitalFaceplateGroups } from '../faceplate/faceplateSelectors';
import productDetailMessages from './ProductDetailMessages';
import AmountLabel from '../amount/AmountLabel';
import PromoBanner from '../promoDisplay/PromoBanner';
import { applyActivePromoPreCart } from '../promo/promoModule';
import {
    getActivePromo,
    getDiscountRewardAmount,
    getGogoPromoCondition,
    getIsPromoNudgeActive
} from '../promo/promoSelectors';

const OptionsSection = ({
    isPlastic, intl, discountRewardAmount, activePromo, gogoPromoCondition, isPromoNudgeActive
}) => {
    const dispatch = useDispatch();
    const itemCurrencyList = useSelector(state => getItemCurrencyList(state));
    const isFixedCatalog = useSelector(
        state => (isPlastic ? isFixedPlasticCatalogPresent(state) : isFixedEgcCatalogPresent(state))
    );
    const denominations = useSelector(state => (isPlastic ? getPlasticDenoms(state) : getElectronicDenoms(state)));
    const isSingleDenom = (denominations.size === 1);
    const selectedDenomination = useSelector(state => newItemDenomination(state));
    const currencyCode = useSelector(state => newItemCurrency(state));
    const currencySymbol = getCurrencySymbol(intl, currencyCode);
    const [youPay, setYouPay] = useState(selectedDenomination);
    const hasDiscount = discountRewardAmount < 0;
    
    const [fromPDP, setFromPDP] = useState(false);
    
    const minDenomAllowed = useSelector(state => (isPlastic ? getMinPlasticValue(state) : getMinEgcValue(state)));
    const maxDenomAllowed = useSelector(state => (isPlastic ? getMaxPlasticValue(state) : getMaxEgcValue(state)));
    const amountRange = intl.formatMessage(amountMessages.amountRange, {
        min: getFormattedAmountWithCurrency(intl, minDenomAllowed, currencyCode),
        max: getFormattedAmountWithCurrency(intl, maxDenomAllowed, currencyCode)
    });
    const maxTransaction = useSelector(state => parseFloat(getMaxTransactionAmount(state).toFixed(2)));
    const subTotal = useSelector(state => parseFloat(getSubTotal(state).toFixed(2)));
    const maxDenom = parseFloat(new Big(maxTransaction).minus(subTotal).toFixed(2));
    const [youPayFlag, setYouPayFlag] = useState(true);
    const [isCustomAmountLabel, setIsCustomAmountLabel] = useState(false);
    const [insideCustomAmount, setInsideCustomAmount] = useState(false);

    const availableDenom = useMemo(() => denominations && denominations.filter(value => value <= maxDenom).map((item) => {
        const fractionDigits = getCurrencyFractionDigits(currencyCode);
        const value = fixDecimalFormat(item, fractionDigits);
        return value;
    }).toJS());

    useEffect(() => {
        const isDenomValuePresent = availableDenom && availableDenom.includes(selectedDenomination);
        setIsCustomAmountLabel(isDenomValuePresent);
    }, []);

    const setInsideCustom = () => {
        setInsideCustomAmount(true);
    };

    const amountCurrencyPickerClasses = classNames(
        'amount-currency-picker',
        { 'no-currency-picker': itemCurrencyList.size <= 1 }
    );

    const amountBox = classNames(
        'amount-box',
        { 'no-bg-color': isSingleDenom }
    );

    const amountLabelProps = {
        component: AmountLabel,
        amount: selectedDenomination,
        name: giftCardFieldNames.AMOUNT_FIELD,
        cashbotName: 'amount',
        helper: '',
        prefix: currencySymbol.prefix,
        suffix: currencySymbol.suffix
    };

    const validateYouPay = () => {
        const maxDenomVal = Number(maxDenomAllowed.toString());
        const minDenomVal = Number(minDenomAllowed.toString());
        if (selectedDenomination > maxDenomVal || selectedDenomination < minDenomVal || selectedDenomination > maxDenom) {
            setYouPayFlag(false);
            return false;
        }
        setYouPayFlag(true);
        return true;
    };

    
    const applyCall = () => {
        if (!(gogoPromoCondition && isPromoNudgeActive) && validateYouPay() && selectedDenomination) {
            dispatch(applyActivePromoPreCart(false, false, false, null));
        }
        setFromPDP(true);
        setInsideCustomAmount(false);
    };

    const customAmountFieldProps = {
        name: giftCardFieldNames.AMOUNT_FIELD,
        component: CustomAmount,
        label: intl.formatMessage(amountMessages.customAmountLabel),
        incLabel: intl.formatMessage(amountMessages.customAmountIncrement),
        decLabel: intl.formatMessage(amountMessages.customAmountDecrement),
        cashbotName: 'amount',
        currencyCode,
        intl,
        hideAmountInput: isFixedCatalog,
        format: value => customAmountNumberFormatter(intl, value, currencyCode, true),
        amountRange,
        onBlur: applyCall,
        onChange: setInsideCustom
    };

    const denomPickerProps = {
        name: giftCardFieldNames.AMOUNT_FIELD,
        denominations,
        currencyCode,
        maxDenom,
        doFocus: false,
        intl
    };

    const amountMesaage = isSingleDenom ? productDetailMessages.signleDenomLabel : amountMessages.title;
    const containerProps = {
        heading: intl.formatMessage(amountMesaage),
        HeadingElement: 'h1'
    };

    const cutomAmountEventHandler = () => {
        setIsCustomAmountLabel(false);
    };

    
    useEffect(() => {
        if (!(gogoPromoCondition && isPromoNudgeActive) && activePromo && !activePromo.isEmpty() && !hasDiscount) {
            dispatch(applyActivePromoPreCart(false, false, false, null));
            setFromPDP(true);
        }
    }, [activePromo]);

    
    useEffect(() => {
        if (!insideCustomAmount && validateYouPay() && !(gogoPromoCondition && isPromoNudgeActive) && selectedDenomination) {
            dispatch(applyActivePromoPreCart(false, false, false, null));
            setFromPDP(true);
        }
    }, [selectedDenomination]);


    
    useEffect(() => {
        if (fromPDP) {
            const grandTotal = Number(selectedDenomination) + Number(discountRewardAmount);
            setYouPay(grandTotal);
            setFromPDP(false);
        }
    }, [discountRewardAmount]);

    return (
        <div className='container'>
            <h1>{intl.formatMessage(productDetailMessages.title)}</h1>
            <PromoBanner />
            <div className="options">
                <CardType />
                <div className={amountBox}>
                    <Container {...containerProps} />
                    <div className='amount-picker'>
                        { !isSingleDenom
                            ? <DenomPickerField {...denomPickerProps} />
                            : <Field {...amountLabelProps} /> }
                        <div className={amountCurrencyPickerClasses}>
                            {isFixedCatalog
                                ? <React.Fragment></React.Fragment>
                                : (
                                    <React.Fragment>
                                        {isCustomAmountLabel
                                            ? (
                                                <button
                                                    className='custom-amount-overlay show'
                                                    onClick={cutomAmountEventHandler}
                                                    type='button'
                                                >
                                                    {intl.formatMessage(productDetailMessages.customAmountLabel)}
                                                </button>
                                            )
                                            : (
                                                <React.Fragment>
                                                    <span className='cutom-amount-wrapper show'>
                                                        <Field {...customAmountFieldProps} />
                                                    </span>
                                                </React.Fragment>
                                            )}
                                        <span className='amount-range'>
                                            {intl.formatMessage(productDetailMessages.priceRange)}
                                            {' '}
                                            {amountRange}
                                        </span>
                                        { !(gogoPromoCondition && isPromoNudgeActive) && youPayFlag
                                            && hasDiscount && selectedDenomination && (
                                            <span>
                                                You Pay
                                                {' '}
                                                {getFormattedAmountWithCurrency(intl, youPay, currencyCode)}
                                            </span>
                                        )}

                                    </React.Fragment>
                                )
                            }
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

OptionsSection.propTypes = {
    intl: intlShape.isRequired,
    isPlastic: PropTypes.bool,
    activePromo: PropTypes.string,
    discountRewardAmount: PropTypes.number,
    gogoPromoCondition: PropTypes.object,
    isPromoNudgeActive: PropTypes.bool
};

OptionsSection.defaultProps = {
    isPlastic: false,
    activePromo: null,
    discountRewardAmount: 0,
    gogoPromoCondition: null,
    isPromoNudgeActive: false
};

const mapStateToProps = state => ({
    groups: getDigitalFaceplateGroups(state),
    groupOrdinal: newItemSelectedGroupOrdinal(state),
    discountRewardAmount: getDiscountRewardAmount(state),
    activePromo: getActivePromo(state),
    gogoPromoCondition: getGogoPromoCondition(state),
    isPromoNudgeActive: getIsPromoNudgeActive(state)
});

export default connect(mapStateToProps)(injectIntl(OptionsSection));
